/**
 * The Pemmican Popup's free bar, in the bag.
 *
 * WHY THIS EXISTS. The popup promised "a free bar with your first order" and then put
 * nothing anywhere the customer could see it: the promise lived only as a customer tag,
 * which neither the cart nor checkout reads, and which a logged-out shopper's storefront
 * cannot read at all. Some customers went looking for the bar, couldn't find it, and
 * abandoned their orders over it.
 *
 * So the bar is now a real line. A winning claim adds "Free Pemmican Bar (Berry)" to the
 * bag — its own unlisted product, SKU PB-BER-01-FREE, because the real single-bar variants
 * are bundle components the storefront refuses to sell ("Cannot find variant") — flagged
 * with the hidden line property _pp_free_bar, and applies the shared code (FREEBAR43, a
 * Buy X Get Y in Shopify: any one other item, that bar free, once per customer).
 * The cart shows the line at £0 under "Your free Berry bar", and checkout shows the same
 * discount natively. Nothing here decides who gets a bar: Shopify adjudicates the code,
 * exactly as at checkout.
 *
 * THREE THINGS THIS HAS TO GET RIGHT, all invisible on screen when they go wrong:
 *
 * 1. Other codes. /cart/update.js REPLACES the discount set (verified live, see
 *    assets/trial-pack-offer.js), so the request carries every code the cart already
 *    honours. And FREESHIPNEW goes in with ours: assets/free-shipping-welcome.js skips
 *    any cart that already holds a code, so if FREEBAR43 lands first, a new customer
 *    would silently lose the free shipping they were promised.
 * 2. Only ever acting on a claim. The product page is reachable by link, and somebody who
 *    lands on it is not claiming anything; the code is only sent when a claim is on
 *    record, the flagged line is in the bag, or the code is already there (the email link
 *    applies it itself).
 * 3. Not nagging after the order. The add-back row offers the bar again only while the
 *    bag is the one it was added to. A new cart token after the bar was in the bag means
 *    the cart was checked out (or expired), and the record retires.
 *
 * Config comes from window.PPFreeBarConfig, emitted by layout/theme.liquid from the
 * "Pemmican popup" theme settings. The pure helpers below are pulled out and exercised
 * by tests/free-bar-offer.test.js — change one, change the other.
 *
 * Debug: ?ppdebug=1, or localStorage.ppFreeBarDebug = '1'.
 */

(function () {
  'use strict';

  var CONFIG = window.PPFreeBarConfig || {};
  var CODE = String(CONFIG.CODE || '').trim();
  var VARIANT = String(CONFIG.VARIANT_ID || '').trim();
  var SHIP_CODE = String(CONFIG.SHIP_CODE || '').trim();
  if (!CODE || !VARIANT) return;

  var KEY = 'pp_free_bar_v1';
  var PROP = '_pp_free_bar';
  /* A claim is "with your first order", not forever. Long enough for a slow decider,
     short enough that a forgotten record cannot resurface a year on. */
  var MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000;

  var DEBUG = false;
  try {
    DEBUG = window.location.search.indexOf('ppdebug=1') !== -1 || localStorage.getItem('ppFreeBarDebug') === '1';
  } catch (e) {}
  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[free-bar-offer]');
    console.log.apply(console, args);
  }

  /* ── pure helpers (tested) ─────────────────────────────────────────────────────── */

  function barLines(cart, variantId) {
    return (cart && Array.isArray(cart.items) ? cart.items : []).filter(function (item) {
      return item && String(item.variant_id) === String(variantId);
    });
  }

  function isFlagged(item) {
    return !!(item && item.properties && item.properties[PROP]);
  }

  function hasCode(cart, code) {
    var want = String(code).toUpperCase();
    return (cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : []).some(function (d) {
      return d && String(d.code || '').toUpperCase() === want;
    });
  }

  /**
   * The full discount set to send, or null for NO REQUEST — the common case.
   *
   * ours: is there a claim to act on (a record, the flagged line, or the code already on
   * the cart)? shipEligible: may FREESHIPNEW ride along (feature on, no orders, not
   * refused before)? The ship code is only repaired when ours is being sent or is
   * already there — the rest of the time that is free-shipping-welcome.js's business.
   */
  function codesFor(cart, opts) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var code = opts.code;
    var ship = opts.shipCode;

    var bars = barLines(cart, opts.variantId);
    var oursPresent = hasCode(cart, code);
    var wantOurs = bars.length > 0 && (opts.claimed || oursPresent || bars.some(isFlagged));
    if (!wantOurs) return null;

    var keep = [];
    (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).forEach(function (d) {
      /* applicable false is a code Shopify is not honouring right now. Ours is the
         exception: with only the bar in the bag it is legitimately inapplicable (the
         customer has not bought the "X" yet), and dropping it would mean re-sending it
         on every add. Everyone else's refused codes are dropped, as trial-pack does. */
      if (!d || typeof d.code !== 'string') return;
      if (d.applicable === true || d.code.toUpperCase() === code.toUpperCase()) keep.push(d.code);
    });
    var upper = keep.map(function (c) { return c.toUpperCase(); });

    var add = [];
    if (upper.indexOf(code.toUpperCase()) === -1) add.push(code);
    if (ship && opts.shipEligible && upper.indexOf(ship.toUpperCase()) === -1) add.push(ship);

    if (!add.length) return null;
    return keep.concat(add);
  }

  /**
   * What the add-back row should do: 'show', 'hide', or 'retire' (forget the claim).
   */
  function addBackState(cart, rec, now, variantId) {
    if (!rec || !cart) return 'hide';
    if (rec.at && now - rec.at > MAX_AGE_MS) return 'retire';
    var inBag = barLines(cart, variantId).length > 0;
    if (inBag) return 'hide';
    /* The bar was in a bag and this is a different bag: that cart became an order, or
       Shopify expired it. Either way, offering a bar again risks charging somebody
       £4.80 at checkout for a code their email has already used. */
    if (rec.added && rec.cart_token && cart.token && rec.cart_token !== cart.token) return 'retire';
    return 'show';
  }

  /* ── storage ─────────────────────────────────────────────────────────────────── */

  function load() {
    try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }
  function save(rec) {
    try { localStorage.setItem(KEY, JSON.stringify(rec)); } catch (e) {}
  }
  function forget() {
    try { localStorage.removeItem(KEY); } catch (e) {}
  }

  function hasOrders() {
    return !!(window.pemmicanCustomer && window.pemmicanCustomer.hasOrders);
  }
  /* The bar has already been delivered on an order. Existing customers can claim one
     too now, so having orders no longer retires a claim; having had the bar does. */
  function hadFreeBar() {
    return !!(window.pemmicanCustomer && window.pemmicanCustomer.hadFreeBar);
  }
  function shipEligible() {
    if (!SHIP_CODE || hasOrders()) return false;
    var fsw = window.FreeShipWelcome;
    /* No FreeShipWelcome at all means the feature is switched off in theme settings. */
    if (!fsw || typeof fsw.isUsed !== 'function') return false;
    return !fsw.isUsed();
  }

  /* ── the cart ────────────────────────────────────────────────────────────────── */

  function onCartPage() {
    return /(^|\/)cart\/?$/.test(window.location.pathname);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; });
  }

  /* Sections the cart page re-renders, same pairs assets/cart.js uses. */
  function redrawTargets() {
    if (!onCartPage()) return [];
    return [
      { id: 'main-cart-items', selector: '.js-contents' },
      { id: 'main-cart-footer', selector: '.cart__blocks' },
    ]
      .map(function (part) {
        var node = document.getElementById(part.id);
        part.section = node && node.dataset ? node.dataset.id : null;
        return part.section ? part : null;
      })
      .filter(Boolean);
  }

  function applySections(sections, targets) {
    targets.forEach(function (part) {
      var target = document.getElementById(part.id);
      var markup = sections && sections[part.section];
      if (!target || typeof markup !== 'string') return;
      var source = new DOMParser().parseFromString(markup, 'text/html').querySelector(part.selector);
      var destination = target.querySelector(part.selector);
      if (source && destination) destination.innerHTML = source.innerHTML;
    });
    /* The empty-cart layout is a class on <cart-items>; a bag that just gained its
       first line needs it off or the lines render inside a hidden form. */
    var shell = document.querySelector('cart-items');
    if (shell && targets.length) shell.classList.remove('is-empty');
  }

  function postUpdate(body) {
    var url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    var targets = redrawTargets();
    if (targets.length) {
      body.sections = targets.map(function (p) { return p.section; }).join(',');
      body.sections_url = window.location.pathname;
    }
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (state) {
        if (state && state.sections) applySections(state.sections, targets);
        return state;
      });
  }

  /* Header-home's own count and Dawn's bubble, as assets/pdp2-configurator.js does it.
     No reload fallback here: this can run with the popup open on top of the page. */
  var COUNT_SELECTORS = [
    '.header-home__cart-count',
    '#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]',
    '.cart-count-bubble span[aria-hidden="true"]',
  ];
  function paintCount(cart) {
    if (!cart) return;
    var n = cart.item_count, updated = 0;
    COUNT_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (node) { node.textContent = n; updated++; });
    });
    if (!updated && n > 0) {
      var link = document.querySelector('.header-home__cart');
      if (link) {
        var span = document.createElement('span');
        span.className = 'header-home__cart-count';
        span.textContent = n;
        link.appendChild(span);
      }
    }
  }

  function paintAddBack(cart) {
    var row = document.querySelector('[data-pp-free-bar-addback]');
    var rec = load();
    if (hadFreeBar()) { forget(); rec = null; }
    var verdict = addBackState(cart, rec, Date.now(), VARIANT);
    log('add-back', verdict, rec);
    if (verdict === 'retire') forget();
    if (row) row.hidden = verdict !== 'show';
  }

  var busy = false;

  /**
   * Read the cart, record that the bar is in it, put the codes right, paint.
   * Never throws into the page: a code that fails must not cost the customer anything
   * they asked for.
   */
  function sync() {
    if (busy) return Promise.resolve(null);
    busy = true;
    return getCart()
      .then(function (cart) {
        if (!cart) return null;
        var rec = load();
        if (rec && barLines(cart, VARIANT).length) {
          if (!rec.added || rec.cart_token !== cart.token) {
            rec.added = true;
            rec.cart_token = cart.token;
            save(rec);
          }
        }
        var wanted = codesFor(cart, {
          code: CODE,
          variantId: VARIANT,
          shipCode: SHIP_CODE,
          shipEligible: shipEligible(),
          claimed: !!rec && !hadFreeBar(),
        });
        log('cart', { codes: cart.discount_codes, bars: barLines(cart, VARIANT).length, send: wanted });
        if (!wanted) return cart;
        return postUpdate({ discount: wanted.join(',') }).then(function (state) {
          log('after', state && state.discount_codes);
          return state || cart;
        });
      })
      .then(function (cart) {
        paintCount(cart);
        paintAddBack(cart);
        return cart;
      })
      .catch(function (e) {
        console.error('free-bar-offer:', e);
        return null;
      })
      .then(function (cart) {
        busy = false;
        return cart;
      });
  }

  /** Put the bar in the bag. Shared by the popup's claim and the add-back row. */
  function addBar() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(VARIANT), quantity: 1, properties: (function () {
        var p = {}; p[PROP] = 'popup'; return p;
      })() }] }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/add ' + r.status);
        return r.json();
      })
      .then(function () { return sync(); })
      .then(function (cart) {
        /* On the cart page the new line has to appear. sync() only redraws when it
           wrote codes, and with the code already on the bag it writes nothing — so ask
           for the sections with an empty update, which changes nothing and renders. */
        if (onCartPage()) return postUpdate({}).then(function (state) { return state || cart; });
        return cart;
      })
      .then(function (cart) {
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pp-free-bar', cartData: cart });
        }
        return cart;
      });
  }

  /**
   * The popup calls this with the claim response's `offer`. Records the claim before the
   * add, so a failed add still leaves the add-back row to offer it on the cart page.
   */
  function claim(offer) {
    if (offer && offer.variant_id && String(offer.variant_id) !== VARIANT) {
      /* Outpost and theme settings disagree about the bar. Trust neither silently. */
      console.warn('free-bar-offer: outpost variant', offer.variant_id, 'is not the theme setting', VARIANT);
    }
    save({ at: Date.now(), added: false, cart_token: '' });
    return getCart().then(function (cart) {
      if (cart && barLines(cart, VARIANT).some(isFlagged)) return sync();
      return addBar();
    }).catch(function (e) {
      console.error('free-bar-offer: add failed', e);
      return sync();
    });
  }

  window.PPFreeBar = { claim: claim, addBar: addBar, sync: sync, CODE: CODE };

  function boot() {
    document.querySelectorAll('[data-pp-free-bar-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        btn.disabled = true;
        addBar().then(function () { btn.disabled = false; }, function () { btn.disabled = false; });
      });
    });
    /* Load-time sync only where there is something to look at: a bag with items, a
       claim on record, or the cart page. Most page views do none of these. */
    if (CONFIG.CART_HAS_ITEMS || load() || onCartPage()) sync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Every AJAX cart change on the site. A beat late on purpose: trial-pack-offer.js
     answers the same event with its own update.js write, and two code writes racing
     each other means the last one wins and the other's code silently vanishes. */
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'pp-free-bar') return;
      setTimeout(sync, 450);
    });
  }
})();
